import React, { useState, useEffect } from 'react';
import '../styles/KmongAutoReply.css';
import ChatList from './components/ChatList';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import StatusBanner from './components/StatusBanner';
import ChatModal from './components/ChatModal';
import LoginModal from './LoginModal';
import { chatAPI } from '../../api/chatAPI';
import { toast } from "../../utils/toast";

function KmongAutoReply() {
  // 상태 관리
  const [user, setUser] = useState(null);
  const [chats, setChats] = useState([]);
  const [stats, setStats] = useState({
    totalChats: 0,
    unreadChats: 0,
    repliedChats: 0,
    replyRate: 0
  });
  const [settings, setSettings] = useState({
    autoReply: true,
    notification: true
  });
  const [monitoring, setMonitoring] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);

  // 초기 데이터 로드
  useEffect(() => {
    loadInitialData();
    
    // 5초마다 폴링
    const interval = setInterval(() => {
      if (monitoring) {
        checkUpdates();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [monitoring]);

  // 초기 데이터 로드
  const loadInitialData = async () => {
    try {
      const [chatsData, statsData, settingsData, userInfo] = await Promise.all([
        chatAPI.getChats(),
        chatAPI.getStats(),
        chatAPI.getSettings(),
        chatAPI.getCurrentUser()
      ]);

      setChats(chatsData.chats || []);
      setStats(statsData);
      setSettings(settingsData);
      setUser(userInfo);
    } catch (error) {
      console.error('데이터 로드 실패:', error);
    }
  };

  // 업데이트 확인
  const checkUpdates = async () => {
    try {
      const data = await chatAPI.checkUpdates();
      if (data.hasUpdates) {
        loadChats();
      }
    } catch (error) {
      console.error('업데이트 확인 실패:', error);
    }
  };

  // 채팅 목록 새로고침
  const loadChats = async () => {
    try {
      const data = await chatAPI.getChats();
      setChats(data.chats || []);
      setStats(prev => ({
        ...prev,
        unreadChats: data.unreadCount
      }));
    } catch (error) {
      console.error('채팅 로드 실패:', error);
    }
  };

  // 로그인
  const handleLogin = async (email, password) => {
    try {
      const data = await chatAPI.login(email, password);
      setUser(data.user);
      setShowLoginModal(false);
      loadInitialData();
    } catch (error) {
      toast('로그인 실패: ' + error.message);
    }
  };

  // 로그아웃
  const handleLogout = async () => {
    try {
      await chatAPI.logout();
      setUser(null);
      setMonitoring(false);
      setChats([]);
    } catch (error) {
      console.error('로그아웃 실패:', error);
    }
  };

  // 모니터링 시작
  const handleStartMonitoring = async () => {
    try {
      await chatAPI.startMonitoring();
      setMonitoring(true);
    } catch (error) {
      toast('모니터링 시작 실패: ' + error.message);
    }
  };

  // 모니터링 중지
  const handleStopMonitoring = async () => {
    try {
      await chatAPI.stopMonitoring();
      setMonitoring(false);
    } catch (error) {
      toast('모니터링 중지 실패: ' + error.message);
    }
  };

  // 채팅 선택
  const handleChatClick = (chat) => {
    setSelectedChat(chat);
    setShowChatModal(true);
  };

  // 답변 전송
  const handleSendReply = async (chatId, message) => {
    try {
      await chatAPI.sendReply(chatId, message);
      setShowChatModal(false);
      loadChats();
      toast('답변이 전송되었습니다!');
    } catch (error) {
      toast('답변 전송 실패: ' + error.message);
    }
  };

  // 답변 재생성
  const handleRegenerateResponse = async (message) => {
    try {
      const data = await chatAPI.generateResponse(message);
      return data.response;
    } catch (error) {
      toast('답변 생성 실패: ' + error.message);
      return null;
    }
  };

  // 설정 변경
  const handleToggleSetting = async (key) => {
    try {
      const newValue = !settings[key];
      await chatAPI.updateSetting(key, newValue);
      setSettings(prev => ({
        ...prev,
        [key]: newValue
      }));
    } catch (error) {
      console.error('설정 변경 실패:', error);
    }
  };

  // 답변 승인
  const handleApprove = async (chatId) => {
    try {
      await chatAPI.approveReply(chatId);
      toast('답변이 전송되었습니다!');
      loadChats();
    } catch (error) {
      toast('답변 전송 실패: ' + error.message);
    }
  };

  // 답변 보류
  const handleReject = async (chatId) => {
    try {
      await chatAPI.rejectReply(chatId);
      toast('답변이 보류되었습니다.');
      loadChats();
    } catch (error) {
      toast('답변 보류 실패: ' + error.message);
    }
  };

  return (
    <div className="kmong-auto-reply">
      <Header 
        user={user}
        onLoginClick={() => setShowLoginModal(true)}
        onLogoutClick={handleLogout}
      />

      <div className="container">
        <StatusBanner 
          monitoring={monitoring}
          user={user}
          onStartMonitoring={handleStartMonitoring}
          onStopMonitoring={handleStopMonitoring}
        />

        <div className="main-grid">
          <ChatList
            chats={chats}
            onChatClick={handleChatClick}
            onApprove={handleApprove}
            onReject={handleReject}
          />

          <Sidebar 
            stats={stats}
            settings={settings}
            onToggleSetting={handleToggleSetting}
          />
        </div>
      </div>

      {/* 로그인 모달 */}
      {showLoginModal && (
        <LoginModal 
          onClose={() => setShowLoginModal(false)}
          onLogin={handleLogin}
        />
      )}

      {/* 채팅 상세 모달 */}
      {showChatModal && selectedChat && (
        <ChatModal 
          chat={selectedChat}
          onClose={() => setShowChatModal(false)}
          onSendReply={handleSendReply}
          onRegenerateResponse={handleRegenerateResponse}
        />
      )}
    </div>
  );
}

export default KmongAutoReply;
